import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconX, IconPaperclip, IconTrash, IconFile, IconPhoto } from '@tabler/icons-react';
import styles from './CreateFeedbackDrawer.module.scss';
import { FeedbackType, FeedbackPriority } from '../../api/model';
import type { CreateFeedbackRequest } from '../../api/model';
import { TYPE_VALUES, PRIORITY_VALUES, TYPE_ICON } from './feedbackMeta';
import {
  ACCEPT_ATTR,
  MAX_FILES,
  MAX_FILE_BYTES,
  isAllowedType,
  formatFileSize,
  fileToPayload,
} from './feedbackAttachments';
import ConfirmDialog from './ConfirmDialog';

interface CreateFeedbackDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFeedbackRequest) => void;
  isSubmitting: boolean;
  error?: string | null;
}

const emptyForm = {
  title: '',
  description: '',
  type: FeedbackType.BUG as FeedbackType,
  priority: FeedbackPriority.MEDIUM as FeedbackPriority,
};

const CreateFeedbackDrawer: React.FC<CreateFeedbackDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  error,
}) => {
  const { t } = useTranslation('feedback');
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState<File[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm);
      setFiles([]);
      setValidationError(null);
      setFileError(null);
      setConfirming(false);
      setPreparing(false);
    }
  }, [isOpen]);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    setFileError(null);
    const next = [...files];
    for (const file of Array.from(list)) {
      if (next.length >= MAX_FILES) {
        setFileError(t('attachments.tooMany', { count: MAX_FILES }));
        break;
      }
      if (file.size > MAX_FILE_BYTES) {
        setFileError(t('attachments.tooLarge', { name: file.name }));
        continue;
      }
      if (!isAllowedType(file.type)) {
        setFileError(t('attachments.unsupported', { name: file.name }));
        continue;
      }
      next.push(file);
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setValidationError(t('create.validation.required'));
      return;
    }
    setValidationError(null);
    setConfirming(true);
  };

  const doSubmit = async () => {
    setConfirming(false);
    setPreparing(true);
    try {
      const attachments = files.length ? await Promise.all(files.map(fileToPayload)) : undefined;
      onSubmit({
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        priority: form.priority,
        attachments,
      });
    } finally {
      setPreparing(false);
    }
  };

  const busy = preparing || isSubmitting;

  return (
    <div className={`${styles.overlay} ${isOpen ? styles.open : ''}`} onClick={onClose}>
      <aside
        className={`${styles.panel} ${isOpen ? styles.open : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>{t('create.title')}</h2>
            <p className={styles.subtitle}>{t('create.subtitle')}</p>
          </div>
          <button className={styles.closeButton} onClick={onClose} type="button" aria-label={t('create.buttons.cancel')}>
            <IconX size={18} />
          </button>
        </header>

        <form className={styles.form} onSubmit={handleFormSubmit}>
          <div className={styles.field}>
            <label htmlFor="fb-title">{t('create.fields.title')}</label>
            <input
              id="fb-title"
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={t('create.fields.titlePlaceholder')}
              autoFocus
            />
          </div>

          <div className={styles.field}>
            <label>{t('create.fields.type')}</label>
            <div className={styles.typeGrid}>
              {TYPE_VALUES.map((type) => {
                const Icon = TYPE_ICON[type];
                return (
                  <button
                    key={type}
                    type="button"
                    className={`${styles.typeOption} ${form.type === type ? styles.typeActive : ''}`}
                    onClick={() => setForm((f) => ({ ...f, type }))}
                  >
                    <Icon size={16} />
                    <span>{t(`types.${type}`)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.field}>
            <label htmlFor="fb-priority">{t('create.fields.priority')}</label>
            <select
              id="fb-priority"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as FeedbackPriority }))}
            >
              {PRIORITY_VALUES.map((priority) => (
                <option key={priority} value={priority}>
                  {t(`priorities.${priority}`)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="fb-description">{t('create.fields.description')}</label>
            <textarea
              id="fb-description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={t('create.fields.descriptionPlaceholder')}
              rows={6}
            />
          </div>

          <div className={styles.field}>
            <label>{t('create.fields.attachments')}</label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              onChange={(e) => addFiles(e.target.files)}
              hidden
            />
            <button type="button" className={styles.attachButton} onClick={() => fileInputRef.current?.click()}>
              <IconPaperclip size={16} />
              {t('detail.attach')}
            </button>
            <p className={styles.attachHint}>{t('create.attachHint')}</p>
            {files.length > 0 && (
              <ul className={styles.fileList}>
                {files.map((file, index) => (
                  <li key={`${file.name}-${index}`} className={styles.fileItem}>
                    {file.type.startsWith('image/') ? <IconPhoto size={16} /> : <IconFile size={16} />}
                    <span className={styles.fileName}>{file.name}</span>
                    <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                    <button type="button" className={styles.fileRemove} onClick={() => removeFile(index)}>
                      <IconTrash size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {fileError && <p className={styles.error}>{fileError}</p>}
          </div>

          {(validationError || error) && <p className={styles.error}>{validationError || error}</p>}

          <div className={styles.footer}>
            <button className={styles.cancelButton} type="button" onClick={onClose}>
              {t('create.buttons.cancel')}
            </button>
            <button className={styles.submitButton} type="submit" disabled={busy}>
              {busy ? t('create.buttons.submitting') : t('create.buttons.submit')}
            </button>
          </div>
        </form>
      </aside>

      <ConfirmDialog
        open={confirming}
        title={t('confirm.submit.title')}
        message={t('confirm.submit.body')}
        confirmLabel={t('confirm.submit.confirm')}
        onCancel={() => setConfirming(false)}
        onConfirm={doSubmit}
      />
    </div>
  );
};

export default CreateFeedbackDrawer;
